/**
 * Career persist serialize.
 *
 * The desk still owns the ticket board and still writes one zustand blob.
 * When Desk Ops prefers Rust, a Tauri command produces that JSON. Any miss
 * fail-closes to JSON.stringify — never a second store, never a half-write.
 */

import { recordDiagnostic } from "./diagnosticsLog";

export type CareerFlushSerializer = (value: unknown) => Promise<string>;

export type CareerFlushHooks = {
  serialize?: CareerFlushSerializer;
  onFallback?: (message: string) => void;
};

export type CareerFlushFingerprint = {
  version: number | null;
  stateKeys: number;
  missions: string[];
  flightLogs: string[];
  inbox: string[];
  fleet: string[];
  telemetryTrips: string[];
  telemetrySession: string;
};

export type SerializeCareerPersistResult = { ok: true; json: string } | { ok: false };

const IDLE_LINE = "Career flush stays on TypeScript until Prefer Rust is on.";

let hooks: CareerFlushHooks = {};
let line = IDLE_LINE;
let lastNotified = "";
const listeners = new Set<() => void>();

export function registerCareerFlushHooks(next: CareerFlushHooks): void {
  hooks = next;
}

export function clearCareerFlushHooks(): void {
  hooks = {};
}

export function resetCareerFlushForTests(): void {
  hooks = {};
  line = IDLE_LINE;
  lastNotified = "";
  for (const listener of listeners) listener();
}

export function getCareerFlushStatusLine(): string {
  return line;
}

export function subscribeCareerFlushStatus(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function publish(next: string, notify: boolean): void {
  const changed = line !== next;
  line = next;
  if (notify) {
    if (lastNotified !== next) {
      lastNotified = next;
      hooks.onFallback?.(next);
    }
  } else {
    lastNotified = "";
  }
  if (changed) {
    for (const listener of listeners) listener();
  }
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

/** Desk Ops default is off — missing / junk stays on the TypeScript persist path. */
export function readPreferRustCareerFlush(value: unknown): boolean {
  const root = asRecord(value);
  if (!root) return false;
  const state = asRecord(root.state) ?? root;
  const company = asRecord(state.company);
  const behavior = asRecord(company?.deskBehavior);
  return behavior?.preferRustCareerFlush === true;
}

function jsonKeyCount(state: Record<string, unknown>): number {
  let count = 0;
  for (const key of Object.keys(state)) {
    const row = state[key];
    if (row === undefined || typeof row === "function") continue;
    count += 1;
  }
  return count;
}

function stamp(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function rowStamp(row: unknown, fields: readonly string[]): string {
  const rec = asRecord(row);
  if (!rec) return "";
  return fields.map((field) => stamp(rec[field])).join("|");
}

function stampsOf(state: Record<string, unknown>, key: string, fields: readonly string[]): string[] {
  const raw = state[key];
  if (!Array.isArray(raw)) return [];
  return raw.map((row) => rowStamp(row, fields));
}

function telemetryOf(state: Record<string, unknown>): { trips: string[]; session: string } {
  const tel = asRecord(state.tailTelemetry);
  if (!tel) return { trips: [], session: "" };
  const trips = Array.isArray(tel.trips) ? tel.trips.map((row) => rowStamp(row, ["id", "registration"])) : [];
  const session = asRecord(tel.session);
  const sessionStamp = session ? rowStamp(session, ["registration", "startedAtUtc", "lastAtUtc"]) : "";
  return { trips, session: sessionStamp };
}

function stateOf(value: unknown): Record<string, unknown> | null {
  const root = asRecord(value);
  if (!root) return null;
  return asRecord(root.state) ?? root;
}

export function careerFlushFingerprint(value: unknown): CareerFlushFingerprint | null {
  const root = asRecord(value);
  const state = stateOf(value);
  if (!root || !state) return null;
  const version = typeof root.version === "number" && Number.isFinite(root.version) ? root.version : null;
  const telemetry = telemetryOf(state);
  return {
    version,
    stateKeys: jsonKeyCount(state),
    missions: stampsOf(state, "missions", ["id", "status", "flightNumber", "originIcao", "destIcao"]),
    flightLogs: stampsOf(state, "flightLogs", ["id", "status"]),
    inbox: stampsOf(state, "inbox", ["id"]),
    fleet: stampsOf(state, "fleet", ["id"]),
    telemetryTrips: telemetry.trips,
    telemetrySession: telemetry.session,
  };
}

function sameList(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

/** Parsed Rust JSON must carry the same envelope and ticket identity as the desk. */
export function careerFlushJsonMatches(value: unknown, json: string): boolean {
  const live = careerFlushFingerprint(value);
  if (!live || typeof json !== "string" || !json.trim()) return false;
  let parsed: unknown;
  try {
    parsed = JSON.parse(json) as unknown;
  } catch {
    return false;
  }
  const rust = careerFlushFingerprint(parsed);
  if (!rust) return false;
  return (
    live.version === rust.version &&
    live.stateKeys === rust.stateKeys &&
    live.telemetrySession === rust.telemetrySession &&
    sameList(live.missions, rust.missions) &&
    sameList(live.flightLogs, rust.flightLogs) &&
    sameList(live.inbox, rust.inbox) &&
    sameList(live.fleet, rust.fleet) &&
    sameList(live.telemetryTrips, rust.telemetryTrips)
  );
}

function fallbackMessage(reason: string): string {
  const clean = reason.replace(/\s+/g, " ").trim().slice(0, 160);
  return `CAREER FLUSH  TypeScript fallback used — ${clean || "Rust career flush failed"}`;
}

function typescriptJson(value: unknown): SerializeCareerPersistResult {
  try {
    return { ok: true, json: JSON.stringify(value) };
  } catch (err) {
    console.warn("ns.dispatch.store persist stringify failed softly", err);
    recordDiagnostic({
      module: "persist",
      severity: "error",
      message: err instanceof Error ? err.message : "Career save could not be encoded.",
      stack: err instanceof Error ? err.stack : undefined,
    });
    return { ok: false };
  }
}

/**
 * Prefer Rust only when the blob says so. Failure, a missing command, or a
 * ticket-list mismatch returns the existing JSON.stringify result.
 */
export async function serializeCareerPersistValue(value: unknown): Promise<SerializeCareerPersistResult> {
  if (!readPreferRustCareerFlush(value)) {
    publish("Last flush: TypeScript career persist.", false);
    return typescriptJson(value);
  }
  const serialize = hooks.serialize;
  if (!serialize) {
    publish(fallbackMessage("Rust career flush is not installed on this desk"), true);
    return typescriptJson(value);
  }
  try {
    const json = await serialize(value);
    if (typeof json !== "string" || !careerFlushJsonMatches(value, json)) {
      publish(fallbackMessage("Rust career flush did not match mission, log, inbox, fleet, or telemetry ids"), true);
      return typescriptJson(value);
    }
    publish("Last flush: Rust serialize_career_persist.", false);
    return { ok: true, json };
  } catch (err) {
    const reason = err instanceof Error && err.message ? err.message : "Rust career flush failed";
    recordDiagnostic({
      module: "persist",
      severity: "error",
      message: reason,
      stack: err instanceof Error ? err.stack : undefined,
    });
    publish(fallbackMessage(reason), true);
    return typescriptJson(value);
  }
}
